import React, { useState, useEffect, useContext } from "react";
import { ServerConfigContext } from "../context/ServerConfigContext";
import { useJWTAuth } from "../context/JWTAuthContext";
import { hasGradePermission } from "../utils/gradeSystem";
import { BOARD_CATEGORIES } from "../utils/boardApiService";
import "./BoardSidebar.css";

const BoardSidebar = ({ currentBoard, onBoardChange, isOpen, onToggle }) => {
  const { getServer, isConfigLoaded } = useContext(ServerConfigContext);
  const {
    token: jwtToken,
    user: jwtUser,
    isAuthenticated: isJWTAuthenticated,
  } = useJWTAuth();

  // 관리자 권한 확인 (JWT 사용)
  const currentUser = jwtUser;
  const hasAuth = isJWTAuthenticated;
  const isAdmin =
    currentUser?.role === "admin" || currentUser?.role === "moderator";
  const [boardCategories, setBoardCategories] = useState([]);

  // Authorization 헤더 생성 (JWT 토큰 우선 사용)
  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // JWT 토큰이 있으면 우선 사용, 없으면 기존 AuthContext 토큰 사용
    const authToken = jwtToken || (jwtUser && localStorage.getItem("auth_token"));

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
      console.log("🔑 [BoardSidebar] Authorization 헤더 설정:", {
        tokenSource: jwtToken ? "JWT" : "AuthContext",
        tokenLength: authToken.length,
        tokenStart: authToken.substring(0, 20) + "...",
      });
    } else {
      console.warn("⚠️ [BoardSidebar] 인증 토큰이 없습니다.");
    }

    return headers;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBoards = async () => {
    setLoading(true);
    setError(null);

    const boardServer = getServer("board-api-server");

    if (!boardServer || !boardServer.isActive) {
      console.warn(
        "⚠️ [BoardSidebar] 게시판 서버가 비활성화됨 - 기본 데이터 사용"
      );
      setBoardCategories(Object.values(BOARD_CATEGORIES));
      setLoading(false);
      return;
    }

    console.log("🚀 [BoardSidebar] 게시판 목록 조회 시작...");

    const apiUrl = `/board-api/api/v1/boards`;
    console.log("📞 [BoardSidebar] API 호출:", apiUrl);

    fetch(apiUrl, {
      method: "GET",
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `API 호출 실패: ${response.status} ${response.statusText}`
          );
        }
        return response.json();
      })
      .then((result) => {
        console.log("📋 [BoardSidebar] API 응답:", result);

        if (result.success && result.data && Array.isArray(result.data)) {
          const idMapping = {
            1: "free",
            2: "qna",
            3: "notice",
            4: "frontend",
            5: "backend",
            6: "mobile",
            7: "photo",
            8: "music",
            9: "game",
          };

          const fetchedCategories = result.data
            .map((categoryGroup) => {
              // null인 경우 디버깅 로그
              if (!categoryGroup.boards) {
                console.log(
                  "ℹ️ [BoardSidebar] 빈 카테고리:",
                  categoryGroup.title,
                  "- boards:",
                  categoryGroup.boards
                );
              }

              return {
                title: categoryGroup.title,
                boards: (categoryGroup.boards || [])
                  .map((board) => ({
                    id: idMapping[board.id] || board.id.toString(),
                    serverId: board.id,
                    name: board.name,
                    icon: board.icon || "📝",
                    count: board.post_count || 0,
                    description: board.description,
                    min_read: board.min_read || null,
                    min_write: board.min_write || null,
                    display_order: board.display_order || 0,
                  }))
                  .filter((board) => {
                    // 게시판 읽기 권한 확인
                    const currentUserGrade =
                      jwtUser?.grade ||
                      "newbie";
                    const canRead = hasGradePermission(
                      currentUserGrade,
                      board.min_read
                    );

                    if (!canRead) {
                      console.log(`🔒 [BoardSidebar] 게시판 접근 권한 없음:`, {
                        board: board.name,
                        userGrade: currentUserGrade,
                        requiredGrade: board.min_read,
                      });
                    }

                    return canRead;
                  })
                  .sort((a, b) => a.display_order - b.display_order), // display_order 순서로 정렬
              };
            })
            .filter((category) => category.boards.length > 0); // 빈 카테고리 제거

          setBoardCategories(fetchedCategories);
          console.log(
            "✅ [BoardSidebar] 게시판 목록 로드 완료:",
            fetchedCategories.length,
            "개 카테고리"
          );
        } else {
          console.warn(
            "⚠️ [BoardSidebar] 게시판 목록 조회 실패 - 기본 데이터 사용:",
            result?.message
          );
          setBoardCategories(Object.values(BOARD_CATEGORIES));
        }
      })
      .catch((err) => {
        console.error("❌ [BoardSidebar] 게시판 목록 로드 실패:", err);
        setError(`게시판 목록을 불러올 수 없습니다: ${err.message}`);
        setBoardCategories(Object.values(BOARD_CATEGORIES));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isConfigLoaded) {
      fetchBoards();
    }
  }, [isConfigLoaded]);

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onToggle} />}

      <aside className={`board-sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h2>게시판 목록</h2>
        </div>

        <div className="sidebar-content">
          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
              <button className="retry-btn" onClick={fetchBoards}>
                다시 시도
              </button>
            </div>
          )}

          {loading && boardCategories.length === 0 ? (
            <div className="loading-message">
              <div className="loading-spinner"></div>
              <p>게시판 목록을 불러오는 중...</p>
            </div>
          ) : (
            boardCategories.map((category, index) => (
              <div key={index} className="board-category">
                <h3 className="category-title">{category.title}</h3>
                <ul className="board-list">
                  {category.boards.map((board) => (
                    <li key={board.id} className="board-item">
                      <button
                        className={`board-link ${
                          currentBoard === board.id ? "active" : ""
                        }`}
                        onClick={() => onBoardChange(board.id)}
                        title={board.description}
                      >
                        <div className="board-info">
                          <span className="board-icon">{board.icon}</span>
                          <span className="board-name">{board.name}</span>
                        </div>
                        <span className="board-count">{board.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
};

export default BoardSidebar;
